import React from "react";
import { SkeletonText } from "../UI/DesignStandards/Skeleton";
import { Pagination, formatCurrency, formatPosition } from "../UI";
import {
  sanitizeErrorMessage,
  logErrorForDebugging,
} from "../../utility/errorSanitization";

const EventDetailView = ({
  filteredBreakdown,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  viewMode,
}) => {
  // Flatten all events from all bowlers for the detail view
  const allEvents = React.useMemo(() => {
    try {
      if (!filteredBreakdown || filteredBreakdown.length === 0) {
        return [];
      }
      return filteredBreakdown.flatMap((bowler) =>
        bowler.events.map((event) => ({
          ...event,
          bowlerId: bowler.bowlerId,
          bowlerName: bowler.bowlerName,
        }))
      );
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "process event data");
      logErrorForDebugging(err, "process event data", sanitizedMessage);
      console.error("Event processing failed:", sanitizedMessage);
      return []; // Return empty array on error to prevent crashes
    }
  }, [filteredBreakdown]);

  // Apply pagination to the flattened events
  const currentPageEvents = React.useMemo(() => {
    try {
      if (itemsPerPage === "All") {
        return allEvents;
      }
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return allEvents.slice(startIndex, endIndex);
    } catch (err) {
      const sanitizedMessage = sanitizeErrorMessage(err, "paginate event data");
      logErrorForDebugging(err, "paginate event data", sanitizedMessage);
      console.error("Event pagination failed:", sanitizedMessage);
      return allEvents; // Return all events on error to prevent crashes
    }
  }, [allEvents, currentPage, itemsPerPage]);

  if (loading) {
    return (
      <div className="card-board overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="min-w-full divide-y divide-brand-charcoal/10"
            role="table"
            aria-label="Prize breakdown by event"
          >
            <thead className="bg-brand-charcoal">
              <tr>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Bowler
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Event
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Position
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Prize Amount
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Event Type
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Team
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-brand-charcoal/10">
              {Array.from({ length: 10 }).map((_, index) => (
                <tr
                  key={index}
                  className={index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"}
                >
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <SkeletonText
                      size="xs"
                      className="h-3 sm:h-4 w-20 sm:w-32 flex-shrink-0"
                    />
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <SkeletonText
                      size="xs"
                      className="h-3 sm:h-4 w-24 sm:w-40 flex-shrink-0"
                    />
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <SkeletonText
                      size="xs"
                      className="h-3 sm:h-4 w-12 sm:w-16 flex-shrink-0"
                    />
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <SkeletonText
                      size="xs"
                      className="h-3 sm:h-4 w-16 sm:w-24 flex-shrink-0"
                    />
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <SkeletonText
                      size="xs"
                      className="h-3 sm:h-4 w-16 sm:w-20 flex-shrink-0"
                    />
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <SkeletonText
                      size="xs"
                      className="h-3 sm:h-4 w-16 sm:w-24 flex-shrink-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!filteredBreakdown || filteredBreakdown.length === 0) {
    return (
      <div className="card-board text-center py-12">
        <h3 className="heading-display text-lg mb-2">No prizes found</h3>
        <p className="text-brand-muted">
          Try a different search or event filter.
        </p>
      </div>
    );
  }

  if (viewMode === "cards") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentPageEvents.map((event, index) => (
          <div
            key={`${event.bowlerId}-${event.teamId || "no-team"}-${
              event.eventId || index
            }`}
            className="card-board p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="heading-display text-lg mb-1">
                  {event.bowlerName || "Unknown Bowler"}
                </h3>
                <p className="text-sm text-brand-muted mb-2">
                  {event.eventName || "Unknown Event"}
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-score font-bold text-brand-charcoal">
                  {formatCurrency(event.prize)}
                </div>
                <div className="text-sm font-score text-brand-muted">
                  {formatPosition(event.position)}
                </div>
              </div>
            </div>

            <div className="space-y-2 text-sm text-brand-muted">
              <div className="flex justify-between">
                <span>Event Type:</span>
                <span className="font-medium">
                  {event.isIndividualEvent ? "Individual" : "Team"}
                </span>
              </div>
              {event.teamName && (
                <div className="flex justify-between">
                  <span>Team:</span>
                  <span className="font-medium">{event.teamName}</span>
                </div>
              )}
              {event.tieDescription && (
                <div className="flex justify-between">
                  <span>Tie:</span>
                  <span className="font-medium">{event.tieDescription}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="card-board overflow-hidden">
        <div className="overflow-x-auto">
          <table
            className="min-w-full divide-y divide-brand-charcoal/10"
            role="table"
            aria-label="Prize breakdown by event"
          >
            <thead className="bg-brand-charcoal">
              <tr>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Bowler
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Event
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Position
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Prize Amount
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Event Type
                </th>
                <th
                  scope="col"
                  className="px-2 sm:px-3 md:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs md:text-sm font-display font-bold tracking-wider uppercase text-brand-cream bg-brand-charcoal"
                >
                  Team
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-brand-charcoal/10">
              {currentPageEvents.map((event, index) => (
                <tr
                  key={`${event.bowlerId}-${event.teamId || "no-team"}-${
                    event.eventId || index
                  }`}
                  className={index % 2 === 0 ? "bg-white" : "bg-brand-cream/60"}
                >
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div
                      className={`text-[10px] sm:text-xs md:text-sm font-medium text-brand-charcoal max-w-[120px] sm:max-w-none truncate`}
                      title={event.bowlerName || "Unknown Bowler"}
                    >
                      {event.bowlerName || "Unknown Bowler"}
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div
                      className={`text-[10px] sm:text-xs md:text-sm text-brand-charcoal max-w-[120px] sm:max-w-none truncate`}
                      title={event.eventName || "Unknown Event"}
                    >
                      {event.eventName || "Unknown Event"}
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="text-[10px] sm:text-xs md:text-sm font-score text-brand-charcoal">
                      {formatPosition(event.position)}
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="text-[10px] sm:text-xs md:text-sm font-score font-semibold text-brand-charcoal">
                      {formatCurrency(event.prize)}
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div className="text-[10px] sm:text-xs md:text-sm text-brand-charcoal">
                      {event.isIndividualEvent ? "Individual" : "Team"}
                    </div>
                  </td>
                  <td className="px-2 sm:px-3 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                    <div
                      className={`text-[10px] sm:text-xs md:text-sm text-brand-charcoal max-w-[120px] sm:max-w-none truncate`}
                      title={event.teamName || "N/A"}
                    >
                      {event.teamName || "N/A"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {allEvents.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            onItemsPerPageChange={onItemsPerPageChange}
          />
        </div>
      )}
    </>
  );
};

export default EventDetailView;
